import {inject, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {IForm} from '../models/IForm';
import {IFormQuestion} from '../models/IFormQuestion';
import {ICustomForm} from '../models/ICustomForm';
import {IFormType} from '../models/IFormType';
import {IFormOption} from '../models/IFormOption';
import {IFormNavigation} from '../models/IFormNavigation';
import {CorporatePermissions} from '../../enums/permissions/CorporatePermissions.enum';
import {CompaniesService} from '../../services/companies.service';
import {PermissionsService} from '../../services/permissions.service';
import {IBusiness} from '../models/IBusiness.interface';
import {IFormResponse} from '../models/IFormResponse';
import {IBusinessRole} from '../models/IBusinessRole';
import {IBusinessShareholder} from '../models/IBusinessShareholder';
import {IUBO} from '../models/IUBO.interface';
import {IFormService} from '../models/IFormService.interface';
import {IFinancial} from '../financials/models/IFinancial.interface';
import {catchError, delay, map, tap} from 'rxjs/operators';
import {IAssignment} from '../assignments-sub-step/models/IAssignment.interface';
import {IAMLReport} from '../models/IAMLReport.interface';
import {IClientAuditReport} from '../models/IClientAuditReport.interface';
import {FormTypeEnum} from '../enums/form-type.enum';
import {IOnboardingFinancial} from '../../auth/models/IOnboardingFinancial.interface';
import {IOnboardingKYC} from '../../auth/models/IOnboardingKYC.interface';
import {IKYCFile} from '../../auth/models/IKYCFile.interface';
import {transformToFormData} from '../../auth/helpers/kyc-files-format.function';
import { Assignment, Position, Salary } from 'src/app/company-management/employee-dashboard/services/organization.service';
import { CreateHolidayDto, HolidayType, MandatoryLeave, PublicHoliday } from 'src/app/company-management/employee-dashboard/services/holiday.service';
import { IUser } from '../planner/models/IUser.interface';
import { IPlannerTask } from '../planner/models/IPlannerTask.interface';
import { IServiceTemplate, IServiceTemplateItem } from '../models/IServiceTemplate.interface';
import { IFamilyMember } from '../models/IFamilyMember.interface';
import { IEmployee } from '../models/IEmployee.interface';
import { IPayrollEmployee } from '../models/IPayrollEmployee.interface';
import { IPayrollEmployeeType } from '../models/IPayrollEmployeeType.interface';
import { IPayrollPayslip } from '../models/IPayrollPayslip.interface';
import { IPayrollPayslipYearly } from '../models/IPayrollPayslipYearly.interface';
import { IEmployeeExtraExpense, IEmployeeExtraExpenseDto } from '../models/IEmployeeExtraExpense.interface';
import { IPayrollPayslipOverride, IPayrollPayslipOverrideBatch } from '../models/IPayrollPayslipOverride.interface';
import { IEmployeeFormOption } from '../models/IEmployeeFormOption.interface';
import { IFormContact } from '../models/IFormContact.interface';
interface Notification {
  id : number,
  isRead:boolean,
  createdAt:string,
  message:string,
  senderAzureObjectId:string,
  azureObjectId:string,
  timeSheetEntryId: number
  submittedFormId: number
  meetingNoteId: number,
  rootMeetingNoteId: number
}
interface Department {
  id: number;
  companyId: number;
  name: string;
  description?: string;
  managerId?: string;
  managerName?: string;
  managerAzureObjectId?: string;
  employeeCount?: number;
  parentDepartmentId?: number;
  parentDepartmentName?: string;
  createdOn?: string;
  updatedOn?: string;
  isActive?: boolean;
}
interface ProfitabilityFilter {
  year?: number;
  month?: number;
  clientSubmittedFormId?: number;
  departmentId?: number;
  serviceTemplateId?: number;
  overheadCostPerEmployee: number;
  pageNumber: number;
  pageSize: number;
}

 interface ProfitabilityReportVm {
  groupingId: number;
  groupingName: string;
  serviceFee: number;
  billableCost: number;
  unbillableAndOverheadCost: number;
  profitAmount: number;
}

export interface PagedResponse<T> {
  items: T[];
  totalCount: number;
  totalPages: number;
}
@Injectable()
export class CorporateFormsService {

  private servicesAssignments = new BehaviorSubject<IFormService[]>([]);
  private serviceManipulator = new BehaviorSubject<{action: string; id: number; service: IFormService}>({} as {action: string; id: number; service: IFormService});

  private httpClient: HttpClient = inject(HttpClient);
  private _url = environment.apiConfig.corporateAPI.endpoint;
  private companiesService = inject(CompaniesService);
  private permissionsService = inject(PermissionsService);

  private adminHeaders = new HttpHeaders({
    'permission' : CorporatePermissions.Admin
  });

  private userHeaders = new HttpHeaders({
    'permission': CorporatePermissions.User
  });

  get companyId() {
    return this.companiesService.managedCompanyId;
  }

  getFormTypes(companyId: number): Observable<IFormType[]> {
    return this.httpClient.get<IFormType[]>(`${this._url}/company/${this.companyId}/forms/types`, {
      headers: this.userHeaders
    });
  }

  getForm(companyId: number, type: number): Observable<IForm> {
    return this.httpClient.get<IForm>(`${this._url}/company/${this.companyId}/forms/type/${type}`, {
      headers: this.userHeaders
    });
  }

  // Returns the SubmittedForm id for the internal-company record of {companyId} —
  // the form that represents the company itself as a client.
  getInternalCompanyFormId(companyId: number): Observable<number> {
    return this.httpClient.get<number>(`${this._url}/company/${companyId}/forms/internal`, {
      headers: this.userHeaders
    });
  }

  // Returns the owning company id + IsInternalCompany flag for a SubmittedForm.
  // Used by the edit-form route guard to switch the tab's managed company when
  // landing on a cross-company internal-company form (so the URL itself stays clean).
  getFormOwningCompany(formId: number): Observable<{ companyId: number; isInternalCompany: boolean }> {
    return this.httpClient.get<{ companyId: number; isInternalCompany: boolean }>(
      `${this._url}/api/forms/${formId}/owning-company`,
      { headers: this.userHeaders }
    );
  }

  getFormQuestions(type: number | null): Observable<IFormQuestion[]> {
    let params = null;
    if (type !== 3 && type !== null) {
      params = !isNaN(type) ? new HttpParams().set('formType', type) : null;
    }
    return this.httpClient.get<IFormQuestion[]>(`${this._url}/company/${this.companyId}/forms/questions`, {
      params: params,
      headers: this.userHeaders
    });
  }

  getForms(
    type: number | null,
    pageNumber: number,
    pageSize: number,
    searchString: string | null,
    client: number | null,
    business: number | null,
   managerAzureUserId?: number | null,
   userObjectID ?:string | null
    ){
    if (type === FormTypeEnum.Introducer) {
      return this.httpClient.get<any[]>(`${this._url}/company/${this.companyId}/introducers`, {
        headers: this.userHeaders
      }).pipe(
        map((introducers) => ({
          items: introducers.map(i => ({
            ...i,
            formTypeId: FormTypeEnum.Introducer,
            answers: []
          })),
          totalCount: introducers.length,
          totalPages: 1
        }))
      );
    }

      let  query;
    let params = new HttpParams();
    console.log(managerAzureUserId)
    if (this.permissionsService.isCorporateAdmin()) {
      query = {
        formType: type === 3 ? null : type,
        searchString: searchString,
        clientStatus: client,
        businessStatus: business,
        isNominee: type === 3,
       // managerAzureId: managerAzureId
      };
    }
    else {
      query = {
        formType: type === 3 ? null : type,
        searchString: searchString,
        clientStatus: client,
        businessStatus: business,
        isNominee: type === 3,
        managerAzureId: userObjectID
      };
    }


    for (const key in query) {
     if (query[key] || query[key] === 0) {
       params = params.set(key, query[key])
     }
    }

    return this.httpClient.post(`${this._url}/company/${this.companyId}/forms`, {
        pageNumber: pageNumber,
        pageSize: pageSize
      },
      {
        params: params,
        headers: this.userHeaders
      });
  }

  getFormToEdit(companyId: number, formId: number) {
    return this.httpClient.get(`${this._url}/company/${this.companyId}/form/${formId}`, {
      headers: this.userHeaders
    });
  }
  getRelatedOfficersBusiness(companyId: number, formId: number) {
    return this.httpClient.get(`${this._url}/company/${this.companyId}/related-officers/${formId}/business-roles`, {
      headers: this.userHeaders
    });
  }
  getRelatedOfficersUbos(companyId: number, formId: number) {
    return this.httpClient.get(`${this._url}/company/${this.companyId}/related-officers/${formId}/ubos`, {
      headers: this.userHeaders
    });
  }
  getRelatedOfficersShareholders(companyId: number, formId: number) {
    return this.httpClient.get(`${this._url}/company/${this.companyId}/related-officers/${formId}/shareholders`, {
      headers: this.userHeaders
    });
  }

  getAllRelatedOfficersBusiness() {
    return this.httpClient.get(`${this._url}/company/${this.companyId}/related-officers/business-roles`, {
      headers: this.userHeaders
    });
  }
  getAllRelatedOfficersUbos() {
    return this.httpClient.get(`${this._url}/company/${this.companyId}/related-officers/ubos`, {
      headers: this.userHeaders
    });
  }
  getAllRelatedOfficersShareholders() {
    return this.httpClient.get(`${this._url}/company/${this.companyId}/related-officers/shareholders`, {
      headers: this.userHeaders
    });
  }

  getNavigation(companyId: number, formId: number, formType?: number): Observable<IFormNavigation> {
    const params = new HttpParams()
      .set('submittedFormId', formId);

    if (formType === 0) {
      return this.httpClient.get<IFormNavigation>(`${this._url}/company/${this.companyId}/form/${formId}/navigation`, {
        headers: this.userHeaders
      });
    } else {
      return this.httpClient.get<IFormNavigation>(`${this._url}/company/${this.companyId}/form/${formId}/navigation`, {
        params: params,
        headers: this.userHeaders
      });
    }
  }

  getFormForDashboard(companyId: number, formType: number) {
    const params = new HttpParams()
      .set('typeId', formType);
    return this.httpClient.get(`${this._url}/company/${this.companyId}/forms/last`, {
      params: params,
      headers: this.userHeaders
    });
  }

  getIndividuals(): Observable<IBusiness[]> {
    return this.httpClient.get<IBusiness[]>(`${this._url}/company/${this.companyId}/customers/individuals`)
  }

  getBusinesses(): Observable<IBusiness[]> {
    return this.httpClient.get<IBusiness[]>(`${this._url}/company/${this.companyId}/customers/businesses`)
  }

  getIntroducers(): Observable<IBusiness[]> {
    return this.httpClient.get<IBusiness[]>(`${this._url}/company/${this.companyId}/introducers`)
  }

  /** Industries configured in the system (compliance custom options). */
  getComplianceIndustries(): Observable<{ id: number; title: string }[]> {
    return this.httpClient.get<{ id: number; title: string }[]>(
      `${this._url}/company/${this.companyId}/compliance/industries`,
    );
  }

  /**
   * Quick-create an introducer with just the basics — the record is stored
   * as `Pending` so it shows up as "uncompleted" on the introducers page
   * for the user to finish filling later.
   */
  createIntroducerQuick(payload: {
    name: string;
    email: string;
    phone?: string | null;
  }): Observable<{ id: number; name: string; email: string; phone: string | null }> {
    const body: any = {
      name: payload.name,
      email: payload.email,
      phone: payload.phone || null,
      type: 0,            // IntroducerType.Individual
      status: 0,          // IntroducerStatus.Pending  ← "uncompleted"
      commissionModel: 0, // Percentage (default)
      commissionRateOrAmount: 0,
      commissionCurrency: 'EUR',
      vatApplicableOnCommission: false,
      allowMultipleClients: false,
    };
    return this.httpClient.post<{ id: number; name: string; email: string; phone: string | null }>(
      `${this._url}/company/${this.companyId}/introducers`, body,
    );
  }

  /** Find existing introducers matching an email or phone (duplicate check). */
  lookupIntroducer(email?: string | null, phone?: string | null): Observable<
    { id: number; name: string; email: string; phone: string | null }[]
  > {
    const params: any = {};
    if (email) params.email = email;
    if (phone) params.phone = phone;
    return this.httpClient.get<any[]>(
      `${this._url}/company/${this.companyId}/introducers/lookup`,
      { params },
    );
  }

  createForm(companyId: number, data, isNominee: boolean, isInternalCompany: boolean, submittedFormId?: number): Observable<IFormResponse> {
    let params = new HttpParams();
    if (submittedFormId) {
      params = params.set('submittedFormId', submittedFormId)
    }
    if (isNominee) {
      params = params.set('isNominee', isNominee)
    }
    if (isInternalCompany) {
      params = params.set('isInternalCompany', isInternalCompany)
    }
    return this.httpClient.post<IFormResponse>(`${this._url}/company/${this.companyId}?submit=${true}`, data, {
      params: params,
      headers: this.userHeaders
    });
  }

  createDraftForm(companyId: number, data, isNominee: boolean, isInternalCompany: boolean, submittedFormId?: number): Observable<IFormResponse> {
    let params = new HttpParams()
    if (submittedFormId) {
     params = params.set('submittedFormId', submittedFormId)
    }
    if (isNominee) {
      params = params.set('isNominee', isNominee)
    }
    if (isInternalCompany) {
      params = params.set('isInternalCompany', isInternalCompany)
    }
    return this.httpClient.post<IFormResponse>(`${this._url}/company/${this.companyId}`, data, {
      params: params,
      headers: this.userHeaders
    });
  }

  submitFilesToForm(companyId: number, formId: number, questionId: number, file) {
    const formData = new FormData();
    formData.append("files", file);
    return this.httpClient.post(`${this._url}/company/${this.companyId}/form/${formId}/question/${questionId}/files`, formData, {
      headers: this.userHeaders
    });
  }

  editForm(companyId: number, formId: number, isSubmit: boolean, data) {
    const params = new HttpParams()
      .set('submit', isSubmit);
    return this.httpClient.put(`${this._url}/company/${this.companyId}/form/${formId}`,data, {
      params: params,
      headers: this.userHeaders
    });
  }

  removeFile(companyId: number, formId: number, questionId: number, fileId: number) {
    return this.httpClient.delete(`${this._url}/company/${this.companyId}/form/${formId}/question/${questionId}/files/${fileId}`, {
      headers: this.userHeaders
    });
  }

  createCustomForm(companyId: number, form: ICustomForm) {
    return this.httpClient.post(`${this._url}/company/${this.companyId}/form/custom`, form, {
      headers: this.adminHeaders
    });
  }

  addNewOption(companyId: number, companyFormId: number, optionId: number, option: IFormOption): Observable<IFormOption> {
    return this.httpClient.post<IFormOption>(`${this._url}/company/${this.companyId}/form/${companyFormId}/question/${optionId}`, option, {
      headers: this.adminHeaders
    });
  }

  addNewQuestion(companyId: number, companyFormId: number, question: IFormQuestion): Observable<IFormQuestion> {
    return this.httpClient.post<IFormQuestion>(`${this._url}/company/${this.companyId}/form/${companyFormId}`, question, {
      headers: this.adminHeaders
    });
  }

  deleteQuestion(companyId: number, companyFormId: number, questionId: number) {
    return this.httpClient.delete(`${this._url}/company/${this.companyId}/form/${companyFormId}/question/${questionId}`, {
      headers: this.adminHeaders
    });
  }

  deleteOption(companyId: number, companyFormId: number, questionId: number, optionId: number) {
    return this.httpClient.delete(`${this._url}/company/${this.companyId}/form/${companyFormId}/question/${questionId}/option/${optionId}`,{
      headers: this.adminHeaders
    });
  }

  setStatus(id: number, status: {clientStatus: number; businessStatuses: any}): Observable<void> {
    return this.httpClient.patch<void>(`${this._url}/company/${this.companyId}/forms/${id}/status`, status);
  }

  // Officers

  getBusinessRoles(id: number): Observable<IBusinessRole[]> {
    return this.httpClient.get<IBusinessRole[]>(`${this._url}/company/${this.companyId}/businesses/${id}/roles`);
  }

  getBusinessShareholders(id: number): Observable<IBusinessShareholder[]> {
    return this.httpClient.get<IBusinessShareholder[]>(`${this._url}/company/${this.companyId}/businesses/${id}/shareholders`);
  }

  getUBOs(id: number): Observable<IUBO[]> {
    return this.httpClient.get<IUBO[]>(`${this._url}/company/${this.companyId}/businesses/${id}/ubos`);
  }

  createUBO(id: number, ubo: IUBO): Observable<{id: number; submittedFormID: number}> {
    return this.httpClient.post<{id: number; submittedFormID: number}>(`${this._url}/company/${this.companyId}/businesses/${id}/ubos`, ubo);
  }

  createBusinessRole(id: number, role: IBusinessRole): Observable<{id: number; submittedFormID: number}> {
    return this.httpClient.post<{id: number; submittedFormID: number}>(`${this._url}/company/${this.companyId}/businesses/${id}/roles`, role);
  }

  createShareholder(id: number, shareholder: IBusinessShareholder): Observable<{id: number; submittedFormID: number}> {
    return this.httpClient.post<{id: number; submittedFormID: number}>(`${this._url}/company/${this.companyId}/businesses/${id}/shareholders`, shareholder)
  }

  updateBusinessRole(id: number, role: IBusinessRole): Observable<number> {
    return this.httpClient.put<number>(`${this._url}/company/${this.companyId}/businesses/${id}/roles/${role.id}`, role)
  }

  updateShareholder(id: number, shareholder: IBusinessShareholder): Observable<number> {
    return this.httpClient.put<number>(`${this._url}/company/${this.companyId}/businesses/${id}/shareholders/${shareholder.id}`, shareholder)
  }

  updateUBO(id: number, ubo: IUBO): Observable<number> {
    return this.httpClient.put<number>(`${this._url}/company/${this.companyId}/businesses/${id}/ubos/${ubo.id}`, ubo);
  }

  deleteBusinessShareholder(id: number, shareholderId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this._url}/company/${this.companyId}/businesses/${id}/shareholders/${shareholderId}`)
  }

  deleteBusinessRole(id: number, roleId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this._url}/company/${this.companyId}/businesses/${id}/roles/${roleId}`)
  }

  deleteUBO(id: number, uboId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this._url}/company/${this.companyId}/businesses/${id}/ubos/${uboId}`);
  }

  //Services

  //Shared

  getServiceActions(): Observable<{service: IFormService, id: number, action: string}> {
    return this.serviceManipulator.asObservable();
  }

  getServicesAssignments(): Observable<IFormService[]> {
    return this.servicesAssignments.asObservable();
  }

  //Individuals
  getIndividualServices(id: number): Observable<IFormService[]> {
    return this.httpClient.get<IFormService[]>(`${this._url}/company/${this.companyId}/customers/individuals/${id}/services`).pipe(
      tap((services) => {
        this.servicesAssignments.next([]);
        this.servicesAssignments.next(services)
      })
    );
  }

  addIndividualService(id: number, service: IFormService): Observable<number> {
    return this.httpClient.post<number>(`${this._url}/company/${this.companyId}/customers/individuals/${id}/services`, service).pipe(
      tap((id) => {
        this.addServiceToAssignments(id, service);
      })
    );
  }

  updateIndividualService(id: number, service: IFormService): Observable<void> {
    return this.httpClient.put<void>(`${this._url}/company/${this.companyId}/customers/individuals/${id}/services/${service.id}`, service);
  }

  deleteIndividualService(individualId: number, service: IFormService) {
    return this.httpClient.delete(`${this._url}/company/${this.companyId}/customers/individuals/${individualId}/services/${service.id}`).pipe(
      tap(() => this.removeServiceFromAssignments(service))
    );
  }

  //Businesses
  getBusinessServices(id: number): Observable<IFormService[]> {
    return this.httpClient.get<IFormService[]>(`${this._url}/company/${this.companyId}/customers/businesses/${id}/services`).pipe(
      tap((services) => {
        this.servicesAssignments.next([]);
        this.servicesAssignments.next(services)
      })
    );
  }

  addBusinessService(id: number, service: IFormService): Observable<number> {
    return this.httpClient.post<number>(`${this._url}/company/${this.companyId}/customers/businesses/${id}/services`, service).pipe(
      tap((id) => this.addServiceToAssignments(id, service))
    );
  }

  updateBusinessService(id: number, service: IFormService): Observable<void> {
    return this.httpClient.put<void>(`${this._url}/company/${this.companyId}/customers/businesses/${id}/services/${service.id}`, service);
  }

  makeSubmittedFormDefaultBank(submittedFormId: number, bankAccountId:string): Observable<void>{

    return this.httpClient.patch<void>(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/default-bank-account`,{bankAccountId:bankAccountId});
  }

  deleteBusinessService(businessId: number, service: IFormService) {
    return this.httpClient.delete(`${this._url}/company/${this.companyId}/customers/businesses/${businessId}/services/${service.id}`).pipe(
      tap(() => this.removeServiceFromAssignments(service))
    );
  }

  // Financials

  getFinancials(submittedFormId: number): Observable<IFinancial[]> {
    return this.httpClient.get<IFinancial[]>(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/financials`);
  }

  addFinancial(submittedFormId: number, financial: IFinancial): Observable<number> {
    return this.httpClient.post<number>(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/financials`, financial);
  }

  editFinancial(submittedFormId: number, financial: IFinancial): Observable<void> {
    return this.httpClient.put<void>(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/financials/${financial.id}`, financial);
  }

  deleteFinancial(submittedFormId: number, id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/financials/${id}`);
  }


  private addServiceToAssignments(serviceId: number, service: IFormService): void {
    this.serviceManipulator.next({service: service, id: serviceId, action: 'Create'})
  }

  private removeServiceFromAssignments(service: IFormService): void {
    this.serviceManipulator.next({service: service, id: service.id, action: 'Remove'})
  }


   editFormService(service: IFormService, previousServiceId: number): void {
    this.serviceManipulator.next({service: service, id: previousServiceId, action: 'Edit'})
  }

  // Assignments

  getAssignments(submittedFormId: number): Observable<IAssignment[]> {
    return this.httpClient.get<IAssignment[]>(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/assignments`);
  }

  addAssignment(submittedFormId: number, assignment: IAssignment): Observable<number> {
    return this.httpClient.post<number>(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/assignments`, assignment);
  }

  editAssignment(submittedFormId: number, assignment: IAssignment): Observable<void> {
    return this.httpClient.put<void>(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/assignments/${assignment.id}`, assignment);
  }

  deleteAssignment(submittedFormId: number, id: number) {
    return this.httpClient.delete(`${this._url}/company/${this.companyId}/forms/${submittedFormId}/assignments/${id}`);
  }

  getAllUbos(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${this._url}/company/${this.companyId}/ubo`);
  }


  //Reports

  getAMLReport(type: FormTypeEnum, isActive: boolean | null, clientType?: string[], nationalities?: [], ubos?: [], selectedClientActivities?: [],
    selectedActivities?: [], selectedBusinessIndustryTypes?: [], selectedBusinessIndustries?: [], selectedRiskLevels?: string,
    selectedPep?: boolean | null, selectedCIP?: boolean | null, selectedHighRiskCountries?: boolean | null, selectedUnderSanctions?: boolean | null,
    selectedprovideAspService?: boolean | null
  ): Observable<IAMLReport[]> {
    let params = new HttpParams();

    if (!isNaN(type) && type !== null) {
      params = params.set('customerType', type.toString());
    }

    if (isActive !== null && isActive !== undefined) {
      params = params.set('status', isActive.toString());
    }
    if (selectedPep !== null && selectedPep !== undefined) {
      params = params.set('pep', selectedPep.toString());
    }
    if (selectedCIP !== null && selectedCIP !== undefined) {
      params = params.set('cip', selectedCIP.toString());
    }
    if (selectedHighRiskCountries !== null && selectedHighRiskCountries !== undefined) {
      params = params.set('highRiskCountries', selectedHighRiskCountries.toString());
    }
    if (selectedUnderSanctions !== null && selectedUnderSanctions !== undefined) {
      params = params.set('underSanctions', selectedUnderSanctions.toString());
    }
    if (selectedprovideAspService !== null && selectedprovideAspService !== undefined) {
      params = params.set('provideAspService', selectedprovideAspService.toString());
    }
    if (clientType && Array.isArray(clientType)) {
      clientType.forEach(type => {
        params = params.append('businessType', type);
      });
    }
    if (nationalities && Array.isArray(nationalities)) {
      nationalities.forEach(nationality => {
        //params = params.append('uboNationality', nationality);
        params = params.append('nationality', nationality);
      });
    }
    if (ubos && Array.isArray(ubos)) {
      ubos.forEach(ubo => {
        params = params.append('uboName', ubo);
      });
    }
    if (selectedClientActivities && Array.isArray(selectedClientActivities)) {
      selectedClientActivities.forEach(clientActivity => {
        params = params.append('clientActivities', clientActivity);
      });
    }
    if (selectedBusinessIndustries && Array.isArray(selectedBusinessIndustries)) {
      selectedBusinessIndustries.forEach(businessIndustryType => {
        params = params.append('businessIndustryType', businessIndustryType);
      });
    }
    if (selectedBusinessIndustryTypes && Array.isArray(selectedBusinessIndustryTypes)) {
      selectedBusinessIndustryTypes.forEach(businessIndustry => {
        params = params.append('businessIndustry', businessIndustry);
      });
    }
    if (selectedActivities && Array.isArray(selectedActivities)) {
      selectedActivities.forEach(activity => {
        params = params.append('ibc', activity);
      });
    }
    if (selectedRiskLevels !== null && selectedRiskLevels !== undefined) {
      if (selectedRiskLevels === 'High') {
        params = params.append('minRiskScore', 71);
        params = params.append('maxRiskScore', 100);


      } else if (selectedRiskLevels === 'Medium') {
        params = params.append('minRiskScore', 19);
        params = params.append('maxRiskScore', 70);
      }
      else if (selectedRiskLevels === 'Low') {
        params = params.append('minRiskScore', 0);
        params = params.append('maxRiskScore', 18);
      }
    }


    return this.httpClient.get<IAMLReport[]>(this._url + `/company/${this.companyId}/reports/aml-clients`, {
      params: params
    });
  }

  getClientAuditReport(year: number | null, type?: FormTypeEnum): Observable<IClientAuditReport[]> {
   let params= new HttpParams();

    if (year !== null && year !== undefined) {
      params = params.set('year', year.toString());
    }

    if (type !== null && type !== undefined) {
      params = params.set('type', type.toString());
    }
    return this.httpClient.get<IClientAuditReport[]>(this._url + `/company/${this.companyId}/reports/audit-clients`, {
      params: params
    });
  }

  getAllNotifications(){
    return this.httpClient.get<Notification[]>(`${this._url}/notifications`);
  }
  updateNotificationBasedOnID(notificationId:number){
    return this.httpClient.put<Notification[]>(`${this._url}/notifications/${notificationId}/read`,{});
  }
  readAllNotifications(){
    return this.httpClient.put<Notification[]>(`${this._url}/notifications/read-all`,{});
  }

  deleteNotificationBasedOnID(notificationId:number){
    return this.httpClient.delete<void>(`${this._url}/notifications/${notificationId}`);
  }

  createNotification(notification: { azureObjectId: string; message: string }): Observable<void> {
    return this.httpClient.post<void>(`${this._url}/notifications`, notification);
  }
 leaveUpdateBasedOnID(timeSheetId:number,status:number){
    return this.httpClient.post<void>(this._url + `/company/5/leaves/${timeSheetId}/status/${status}`,{});
  }

  // Financials

  getOnboardingFinancials(formId: number): Observable<IOnboardingFinancial> {
    return this.httpClient.get<IOnboardingFinancial>(this._url + `/company/${5}/form/${formId}/financials`);
  }

  updateOnboardingFinancial(formId:number, financial: IOnboardingFinancial): Observable<void>{
    return this.httpClient.put<void>(this._url + `/company/${5}/form/${formId}/financials`, financial);
  }

  // KYC
  getKYCFiles(formId: number): Observable<IOnboardingKYC> {
    return this.httpClient.get<IOnboardingKYC>(this._url + `/company/${5}/form/${formId}/kyc-documents`);
  }

  uploadKYCFiles(formId: number, kycDocuments: FormData) {
    return this.httpClient.post<IOnboardingKYC>(this._url + `/company/${5}/form/${formId}/kyc-documents`, kycDocuments);
  }

  cancelRequest(leaveRequestId: number, comments: string, approved: boolean): Observable<void> {
    return this.httpClient.post<void>
    (`${this._url}/api/companies/5/admin/leave-requests/${leaveRequestId}/cancel-decision`, {
      approved: approved,
      comments: comments,
    })
  }

  cancelApprovedRequest(leaveRequestId: number, azureObjectId: string, reason: string): Observable<void> {
    return this.httpClient.post<void>
    (`${this._url}/companies/5/employees/${azureObjectId}/leave/requests/${leaveRequestId}/cancel`, {
      reason: reason
    })
  }

   // Corresponds to GET /api/companies/{companyId}/employees/{employeeAzureObjectId}/LeaveSummary
      getEmployeeLeaves(employeeAzureObjectId: string): Observable<any> {
        let url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave`;

        return this.httpClient.get<any>(url);
      }
  // Corresponds to GET /api/companies/{companyId}/employees/{employeeAzureObjectId}/LeaveSummary
      getEmployeeLeaveSummary(employeeAzureObjectId: string, year?: number): Observable<any> {
        let url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/summary`;
        if (year) {
          url += `?year=${year}`;
        }
        return this.httpClient.get<any>(url);
      }

      // Corresponds to GET /api/companies/{companyId}/employees/{employeeAzureObjectId}/LeaveRequests
    getLeaveRequests(employeeAzureObjectId: string, year?: number): Observable<any[]> {
      let url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/requests`;
      if (year) {
        url += `?year=${year}`;
      }
      return this.httpClient.get<any[]>(url);
    }

    // Corresponds to GET /api/companies/{companyId}/employees/{employeeAzureObjectId}/UpcomingLeaves
    getUpcomingLeaves(employeeAzureObjectId: string): Observable<any[]> {
      const url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/upcoming`;
      return this.httpClient.get<any[]>(url);
    }

    // Corresponds to POST /api/companies/{companyId}/employees/{employeeAzureObjectId}/LeaveRequests/Validate
    validateLeaveRequest(employeeAzureObjectId: string, dto: any): Observable<any> {
      const url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/validate`;
      return this.httpClient.post<any>(url, dto);
    }

    // Corresponds to POST /api/companies/{companyId}/employees/{employeeAzureObjectId}/LeaveRequests
    submitLeaveRequest(employeeAzureObjectId: string, dto: any): Observable<any> {
      const url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/requests`;
      return this.httpClient.post<any>(url, dto);
    }

    // Admin endpoints

    // Corresponds to GET /api/companies/{companyId}/admin/leave-requests/pending
    getPendingLeavesForManager(): Observable<any[]> {
      const url = `${this._url}/api/companies/5/admin/leave-requests/pending`;
      return this.httpClient.get<any[]>(url);
    }

    getProfitabilityReport(companyId ,filters: ProfitabilityFilter): Observable<PagedResponse<ProfitabilityReportVm>> {
      let params = new HttpParams();

      if (filters.year !== undefined && filters.year !== null) {
        params = params.set('Year', filters.year.toString());
      }
      if (filters.month !== undefined && filters.month !== null) {
        params = params.set('Month', filters.month.toString());
      }
      if (filters.clientSubmittedFormId !== undefined && filters.clientSubmittedFormId !== null) {
        params = params.set('ClientSubmittedFormId', filters.clientSubmittedFormId.toString());
      }
      if (filters.departmentId !== undefined && filters.departmentId !== null) {
        params = params.set('DepartmentId', filters.departmentId.toString());
      }
      if (filters.serviceTemplateId !== undefined && filters.serviceTemplateId !== null) {
        params = params.set('ServiceTemplateId', filters.serviceTemplateId.toString());
      }
      if (filters.overheadCostPerEmployee !== undefined && filters.overheadCostPerEmployee !== null) {
        params = params.set('OverheadCostPerEmployee', filters.overheadCostPerEmployee.toString());
      }
      if (filters.pageNumber !== undefined && filters.pageNumber !== null) {
        params = params.set('Pagination.PageNumber', filters.pageNumber.toString());
      }
      if (filters.pageSize !== undefined && filters.pageSize !== null) {
        params = params.set('Pagination.PageSize', filters.pageSize.toString());
      }
      const url = `${this._url}/api/companies/${this.companyId}/reports/profitability`;
      return this.httpClient.get<PagedResponse<ProfitabilityReportVm>>(url, { params });
    }

    getAllEmployeeLeavesForManager(): Observable<any[]> {
      const url = `${this._url}/api/companies/5/admin/leave-requests/all`;
      return this.httpClient.get<any[]>(url);
    }

    // Corresponds to POST /api/companies/{companyId}/admin/leave-requests/{leaveRequestId}/approve
    approveLeave(leaveRequestId: number): Observable<void> {
      const url = `${this._url}/api/companies/5/admin/leave-requests/${leaveRequestId}/approve`;
      return this.httpClient.post<void>(url, null);
    }

    // Corresponds to POST /api/companies/{companyId}/admin/leave-requests/{leaveRequestId}/reject
    rejectLeave(leaveRequestId: number,reasonText: string): Observable<void> {
      const url = `${this._url}/api/companies/5/admin/leave-requests/${leaveRequestId}/reject`;
      return this.httpClient.post<void>(url,{ reasonText });
    }

    // Corresponds to PUT /api/companies/{companyId}/employees/{employeeAzureObjectId}/leave/requests/{leaveRequestId}
    updateLeaveRequest(employeeAzureObjectId: string, leaveRequestId: number, dto: any): Observable<any> {
      const url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/requests/${leaveRequestId}`;
      return this.httpClient.put<any>(url, dto);
    }

    // Corresponds to DELETE /api/companies/{companyId}/employees/{employeeAzureObjectId}/leave/requests/{leaveRequestId}
    deleteLeaveRequest(employeeAzureObjectId: string, leaveRequestId: number): Observable<void> {
      const url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/requests/${leaveRequestId}`;
      return this.httpClient.delete<void>(url);
    }
     // Corresponds to GET /api/companies/{companyId}/employees/{employeeAzureObjectId}/leave/requests/{leaveRequestId}
     getLeaveRequest(employeeAzureObjectId: string, leaveRequestId: number): Observable<void> {
      const url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/requests/${leaveRequestId}`;
      return this.httpClient.get<void>(url);
    }
    getLeaveAllowances(employeeAzureObjectId: string, year?: number): Observable<any> {
      let url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/allowances`;
      if (year) {
        url += `?year=${year}`;
      }
      return this.httpClient.get<any>(url);
    }

    updateLeaveAllowances(employeeAzureObjectId: string, dto: any, year?: number): Observable<any> {
      let url = `${this._url}/companies/5/employees/${employeeAzureObjectId}/leave/allowances`;
      if (year) {
        url += `?year=${year}`;
      }
      return this.httpClient.put<any>(url, dto);
    }



    // Position Management
  getPositions(): Observable<Position[]> {
    return this.httpClient.get<Position[]>(`${this._url}/api/companies/${this.companyId}/organization/positions`);
  }

  getPosition(positionId: number): Observable<Position> {
    return this.httpClient.get<Position>(`${this._url}/api/companies/${this.companyId}/organization/positions/${positionId}`);
  }

  createPosition(position: Position): Observable<Position> {
    return this.httpClient.post<Position>(`${this._url}/api/companies/${this.companyId}/organization/positions`, position);
  }

  updatePosition(positionId: number, position: Position): Observable<Position> {
    return this.httpClient.put<Position>(`${this._url}/api/companies/${this.companyId}/organization/positions/${positionId}`, position);
  }

  deletePosition(positionId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this._url}/api/companies/${this.companyId}/organization/positions/${positionId}`);
  }

  // Salary Management
  getSalaries(organizationId: number, employeeAzureObjectId: string): Observable<Salary[]> {
    return this.httpClient.get<Salary[]>(`${this._url}/api/organizations/${organizationId}/employees/${employeeAzureObjectId}/salaries`);
  }

  createSalary(organizationId: number, employeeAzureObjectId: string, salary: Salary): Observable<Salary> {
    return this.httpClient.post<Salary>(`${this._url}/api/organizations/${organizationId}/employees/${employeeAzureObjectId}/salaries`, salary);
  }

  updateSalary(organizationId: number, employeeAzureObjectId: string, salaryId: number, salary: Salary): Observable<Salary> {
    return this.httpClient.put<Salary>(`${this._url}/api/organizations/${organizationId}/employees/${employeeAzureObjectId}/salaries/${salaryId}`, salary);
  }

  deleteSalary(organizationId: number, employeeAzureObjectId: string, salaryId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this._url}/api/organizations/${organizationId}/employees/${employeeAzureObjectId}/salaries/${salaryId}`);
  }

  // Assignment Management
  getAssignmentsPossitions(employeeAzureObjectId: string): Observable<Assignment[]> {
    return this.httpClient.get<Assignment[]>(`${this._url}/api/companies/${this.companyId}/organization/employees/${employeeAzureObjectId}/assignments`);
  }

  getAllAssignmentsPossitions(): Observable<Assignment[]> {
    return this.httpClient.get<Assignment[]>(`${this._url}/api/companies/${this.companyId}/organization/assignments`);
  }

  createAssignment(assignment: Assignment): Observable<Assignment> {
    return this.httpClient.post<Assignment>(`${this._url}/api/companies/${this.companyId}/organization/assignments`, assignment);
  }

  updateAssignment(assignmentId: number, assignment: Assignment): Observable<Assignment> {
    return this.httpClient.put<Assignment>(`${this._url}/api/companies/${this.companyId}/organization/assignments/${assignmentId}`, assignment);
  }

  deleteAssignmentPossitions(assignmentId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this._url}/api/companies/${this.companyId}/organization/assignments/${assignmentId}`);
  }
  getDepartments(): Observable<Department[]> {
    return this.httpClient.get<Department[]>(
      `${this._url}/api/companies/${this.companyId}/organization/departments`,
      { headers: this.userHeaders }
    );
  }
  getDepartment(departmentId: number): Observable<Department> {
    return this.httpClient.get<Department>(
      `${this._url}/api/companies/${this.companyId}/organization/departments/${departmentId}`,
      { headers: this.userHeaders }
    );
  }
  createDepartment( department: Partial<Department>): Observable<Department> {
    return this.httpClient.post<Department>(
      `${this._url}/api/companies/${this.companyId}/organization/departments`,
      department,
      { headers: this.userHeaders }
    );
  }
  updateDepartment(departmentId: number, department: Partial<Department>): Observable<Department> {
    return this.httpClient.put<Department>(
      `${this._url}/api/companies/${this.companyId}/organization/departments/${departmentId}`,
      department,
      { headers: this.userHeaders }
    );
  }
  deleteDepartment(departmentId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this._url}/api/companies/${this.companyId}/organization/departments/${departmentId}`,
      { headers: this.userHeaders }
    );
  }

// ============================================
// HOLIDAY SERVICE
// ============================================

    // Get all holidays for the company
    getPublicHolidays(year?: number): Observable<PublicHoliday[]> {
      const url = `${this._url}/api/companies/5/holidays`;

      return this.httpClient.get<any[]>(url, { headers: this.userHeaders })
        .pipe(
          map((holidays: any[]) => {
            // Transform API response to match PublicHoliday interface
            const transformed = holidays.map(h => ({
              ...h,
              dateOnly: h.date || h.dateOnly, // Map 'date' to 'dateOnly'
              holidayType: h.type !== undefined
                ? (h.type === 0 ? HolidayType.Public : HolidayType.Mandatory)
                : h.holidayType
            }));

            // Filter by year if specified
            if (year) {
              const filtered = transformed.filter(h => {
                // Use the date field that actually exists
                const dateField = h.date || h.dateOnly;
                if (!dateField) {
                  return false;
                }
                const holidayYear = new Date(dateField).getFullYear();
              
                return holidayYear === year;
              });
              return filtered;
            }

            return transformed;
          }),
          catchError(this.handleError)
        );
    }

    // Get holidays in a date range
    getHolidaysBetweenDates(startDate: Date, endDate: Date): Observable<PublicHoliday[]> {
      const params = {
        startDate: this.formatDate(startDate),
        endDate: this.formatDate(endDate)
      };

      return this.httpClient.get<PublicHoliday[]>(
        `${this._url}/api/companies/5/holidays/range`,
        {
          headers: this.userHeaders,
          params
        }
      ).pipe(catchError(this.handleError));
    }

    // Get count of mandatory holidays
    getMandatoryHolidaysCount(): Observable<number> {
      return this.httpClient.get<number>(
        `${this._url}/api/companies/5/holidays/mandatory-count`,
        { headers: this.userHeaders }
      ).pipe(catchError(this.handleError));
    }

    // Get upcoming holidays (filter from all holidays)
    getUpcomingHolidays(): Observable<PublicHoliday[]> {
      return this.getPublicHolidays().pipe(
        map(holidays => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return holidays.filter(h => new Date(h.dateOnly) >= today)
            .sort((a, b) => new Date(a.dateOnly).getTime() - new Date(b.dateOnly).getTime());
        })
      );
    }

    // Get a specific holiday
    getHolidayById(holidayId: string): Observable<PublicHoliday> {
      return this.httpClient.get<PublicHoliday>(
        `${this._url}/api/companies/5/holidays/${holidayId}`,
        { headers: this.userHeaders }
      ).pipe(catchError(this.handleError));
    }

    // Create a new holiday
    createPublicHoliday(holiday: Partial<PublicHoliday>): Observable<PublicHoliday> {
      const createDto: CreateHolidayDto = {
        Date: this.formatDate(holiday.dateOnly || new Date()),
        Name: holiday.name || '',
        Type: holiday.type || HolidayType.Public,
        IsRecurring: holiday.isRecurring || false
      };

      return this.httpClient.post<PublicHoliday>(
        `${this._url}/api/companies/5/holidays`,
        createDto,
        { headers: this.userHeaders }
      ).pipe(catchError(this.handleError));
    }

    // Update a holiday
    updatePublicHoliday(holidayId: string, holiday: Partial<PublicHoliday>): Observable<PublicHoliday> {
      const updateDto: CreateHolidayDto = {
        Date: this.formatDate(holiday.dateOnly || new Date()),
        Name: holiday.name || '',
        Type: holiday.type || HolidayType.Public,
        IsRecurring: holiday.isRecurring || false
      };

      return this.httpClient.put<PublicHoliday>(
        `${this._url}/api/companies/5/holidays/${holidayId}`,
        updateDto,
        { headers: this.userHeaders }
      ).pipe(catchError(this.handleError));
    }

    // Delete a holiday
    deletePublicHoliday(holidayId: string): Observable<void> {
      return this.httpClient.delete<void>(
        `${this._url}/api/companies/5/holidays/${holidayId}`,
        { headers: this.userHeaders }
      ).pipe(catchError(this.handleError));
    }

    // Check if a specific date is a holiday
    isHoliday(date: Date): Observable<boolean> {
      return this.getPublicHolidays().pipe(
        map(holidays => {
          const dateStr = this.formatDate(date);
          return holidays.some(h => h.dateOnly === dateStr);
        })
      );
    }

    // Import holidays from template (implement based on your backend)
    importHolidaysFromTemplate(country: string, year: number): Observable<PublicHoliday[]> {
      // This might need to be implemented differently based on your backend
      // For now, returning a placeholder
      return this.httpClient.post<PublicHoliday[]>(
        `${this._url}/api/companies/5/holidays/import`,
        { country, year },
        { headers: this.userHeaders }
      ).pipe(catchError(this.handleError));
    }

    // Mandatory Leave Methods
    // Note: These need to be updated when you have the mandatory leave API endpoints
    getMandatoryLeaves(): Observable<MandatoryLeave[]> {
      return this.getPublicHolidays().pipe(
        map((holidays: any[]) => {
          // Filter holidays where type === 1 (Mandatory)
          const mandatoryHolidays = holidays.filter(h =>
            h.type === 1 || h.holidayType === HolidayType.Mandatory
          );

          // Convert to MandatoryLeave format
          return mandatoryHolidays.map(h => this.convertToMandatoryLeave(h));
        })
      );
    }

    getActiveMandatoryLeaves(): Observable<MandatoryLeave[]> {
      return this.getMandatoryLeaves().pipe(
        map(leaves => leaves.filter(l => l.isActive))
      );
    }

    createMandatoryLeave(leave: MandatoryLeave): Observable<MandatoryLeave> {
      // Create as a mandatory holiday
      const holiday: Partial<PublicHoliday> = {
        name: leave.name,
        dateOnly: this.formatDate(leave.startDate),
        type: HolidayType.Mandatory,
        isRecurring: false
      };

      return this.createPublicHoliday(holiday).pipe(
        map(h => this.convertToMandatoryLeave(h))
      );
    }

    updateMandatoryLeave(id: string, leave: MandatoryLeave): Observable<MandatoryLeave> {
      const holiday: Partial<PublicHoliday> = {
        name: leave.name,
        dateOnly: this.formatDate(leave.startDate),
        type: HolidayType.Mandatory,
        isRecurring: false
      };

      return this.updatePublicHoliday(id, holiday).pipe(
        map(h => this.convertToMandatoryLeave(h))
      );
    }

    deleteMandatoryLeave(id: string): Observable<void> {
      return this.deletePublicHoliday(id);
    }

    applyMandatoryLeaveToAll(leaveId: string): Observable<any> {
      // This endpoint might need to be implemented on the backend
      return this.httpClient.post<any>(
        `${this._url}/api/companies/5/holidays/${leaveId}/apply`,
        {},
        { headers: this.userHeaders }
      ).pipe(catchError(this.handleError));
    }

    // Helper methods
    private formatDate(date: Date | string): string {
      if (typeof date === 'string') {
        // If it's already a string, try to parse and reformat
        const d = new Date(date);
        return d.toISOString().split('T')[0];
      }
      return date.toISOString().split('T')[0];
    }

    private convertToMandatoryLeave(holiday: any): MandatoryLeave {
      const dateField = holiday.date || holiday.dateOnly;
      return {
        id: holiday.id,
        name: holiday.name,
        startDate: dateField,
        endDate: dateField,
        description: '',
        leaveType: 1,
        appliesTo: 'all',
        isActive: holiday.isActive !== undefined ? holiday.isActive : true
      };
    }
    private handleError(error: any): Observable<never> {
      return throwError(() => error);
    }
    // Merchant Management
  updateMerchant(submittedFormId: number, merchantId: number): Observable<void> {
    return this.httpClient.patch<void>(
      `${this._url}/company/${this.companyId}/forms/${submittedFormId}/merchant/${merchantId}`,
      { headers: this.userHeaders }
    ).pipe(catchError(this.handleError));
  }

  // Delete submitted form (admin only)
  deleteSubmittedForm(submittedFormId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this._url}/company/${this.companyId}/form/${submittedFormId}`,
      { headers: this.adminHeaders }
    ).pipe(catchError(this.handleError));
  }

// CORRECTED: Add these methods to CorporateFormsService
// This version properly handles the IFormService structure without serviceName/departmentName

// ============================================
// PLANNER TASKS METHODS (CORRECTED)
// ============================================

/**
 * Get all planner tasks for the current company
 */
getPlannerTasks(): Observable<IPlannerTask[]> {
  return this.httpClient.get<IPlannerTask[]>(
    `${this._url}/api/companies/${this.companyId}/planner/tasks`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Get a specific planner task by ID with full details including checklist items
 */
getPlannerTaskById(taskId: number): Observable<IPlannerTask> {
  return this.httpClient.get<IPlannerTask>(
    `${this._url}/api/companies/${this.companyId}/planner/tasks/${taskId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Get planner tasks assigned to the current user
 */
getMyPlannerTasks(): Observable<IPlannerTask[]> {
  return this.httpClient.get<IPlannerTask[]>(
    `${this._url}/api/companies/${this.companyId}/planner/service-tasks/my-tasks`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Create a new planner task
 */
createPlannerTask(task: any): Observable<IPlannerTask> {
  return this.httpClient.post<IPlannerTask>(
    `${this._url}/api/companies/${this.companyId}/planner/tasks`,
    task,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Create a service task (link a planner task to a service)
 */
createServiceTask(dto: {
  serviceId: number;
  plannerTaskId?: number | null;
  priority?: number;
  startDate: string;
  dueDate?: string | null;
  notes?: string | null;
}): Observable<any> {
  return this.httpClient.post<any>(
    `${this._url}/api/companies/${this.companyId}/planner/service-tasks`,
    dto,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Update an existing planner task
 */
updatePlannerTask(task: any): Observable<IPlannerTask> {
  return this.httpClient.put<IPlannerTask>(
    `${this._url}/api/companies/${this.companyId}/planner/tasks/${task.id}`,
    task,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Delete a planner task
 */
deletePlannerTask(taskId: number): Observable<void> {
  return this.httpClient.delete<void>(
    `${this._url}/api/companies/${this.companyId}/planner/tasks/${taskId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Toggle a checklist item completion status
 */
toggleChecklistItem(taskId: number, checklistItemId: number, action: 'complete' | 'uncomplete'): Observable<void> {
  return this.httpClient.post<void>(
    `${this._url}/api/companies/${this.companyId}/planner/service-tasks/${taskId}/checklist-items/${checklistItemId}/${action}`,
    {},
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Assign a user to a planner task (service task)
 */
assignUserToTask(serviceTaskId: number, checklistItemId: number): Observable<void> {
  return this.httpClient.post<void>(
    `${this._url}/api/companies/${this.companyId}/planner/service-tasks/${serviceTaskId}/checklist-items/${checklistItemId}/assign`,
    {},
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Unassign a user from a planner task (service task)
 */
unassignUserFromTask(serviceTaskId: number, checklistItemId: number): Observable<void> {
  return this.httpClient.post<void>(
    `${this._url}/api/companies/${this.companyId}/planner/service-tasks/${serviceTaskId}/checklist-items/${checklistItemId}/unassign`,
    {},
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Get available positions for a service task
 * GET /api/companies/{companyId}/planner/service-tasks/{serviceTaskId}/positions
 */
getPositionsForServiceTask(serviceTaskId: number): Observable<Position[]> {
  return this.httpClient.get<Position[]>(
    `${this._url}/api/companies/${this.companyId}/planner/service-tasks/${serviceTaskId}/positions`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}


copyServices(serviceIds: number[], targetYear: number): Observable<any> {
  const body = {
      serviceIds: serviceIds,
      targetYear: targetYear
  };

  return this.httpClient.post(`${this._url}/company/${this.companyId}/services/transfer`, body);
}

// ============================================
// SERVICE TEMPLATES
// ============================================

getServiceTemplates(): Observable<IServiceTemplate[]> {
  return this.httpClient.get<IServiceTemplate[]>(
    `${this._url}/company/${this.companyId}/service-templates`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

getServiceTemplate(templateId: number): Observable<IServiceTemplate> {
  return this.httpClient.get<IServiceTemplate>(
    `${this._url}/company/${this.companyId}/service-templates/${templateId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

createServiceTemplate(template: IServiceTemplate): Observable<IServiceTemplate> {
  return this.httpClient.post<{ id: number }>(
    `${this._url}/company/${this.companyId}/service-templates`,
    {
      name: template.name,
      description: template.description || '',
      items: template.items
    },
    { headers: this.userHeaders }
  ).pipe(
    map(response => ({ ...template, id: response.id })),
    catchError(this.handleError)
  );
}

updateServiceTemplate(templateId: number, template: IServiceTemplate): Observable<IServiceTemplate> {
  return this.httpClient.put<void>(
    `${this._url}/company/${this.companyId}/service-templates/${templateId}`,
    {
      name: template.name,
      description: template.description || '',
      items: template.items
    },
    { headers: this.userHeaders }
  ).pipe(
    map(() => template),
    catchError(this.handleError)
  );
}

deleteServiceTemplate(templateId: number): Observable<void> {
  return this.httpClient.delete<void>(
    `${this._url}/company/${this.companyId}/service-templates/${templateId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// ============================================
// SERVICE TEMPLATE ITEMS
// ============================================

getServiceTemplateItems(templateId: number): Observable<IServiceTemplateItem[]> {
  return this.httpClient.get<IServiceTemplateItem[]>(
    `${this._url}/company/${this.companyId}/service-templates/${templateId}/items`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

createServiceTemplateItem(templateId: number, item: IServiceTemplateItem): Observable<{ id: number }> {
  return this.httpClient.post<{ id: number }>(
    `${this._url}/company/${this.companyId}/service-templates/${templateId}/items`,
    item,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

updateServiceTemplateItem(templateId: number, itemId: number, item: IServiceTemplateItem): Observable<void> {
  return this.httpClient.put<void>(
    `${this._url}/company/${this.companyId}/service-templates/${templateId}/items/${itemId}`,
    item,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

deleteServiceTemplateItem(templateId: number, itemId: number): Observable<void> {
  return this.httpClient.delete<void>(
    `${this._url}/company/${this.companyId}/service-templates/${templateId}/items/${itemId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// ============================================
// SERVICES BULK OPERATIONS
// ============================================

/**
 * Approve multiple services
 * POST /company/{companyId}/services/approve
 */
approveServices(serviceIds: number[]): Observable<void> {
  return this.httpClient.post<void>(
    `${this._url}/company/${this.companyId}/services/approve`,
    { serviceIds },
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Mark multiple services as paid
 * POST /company/{companyId}/services/mark-paid
 */
markServicesAsPaid(serviceIds: number[]): Observable<void> {
  return this.httpClient.post<void>(
    `${this._url}/company/${this.companyId}/services/mark-paid`,
    { serviceIds },
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Mark multiple services as invoiced
 * POST /company/{companyId}/services/mark-invoiced
 */
markServicesAsInvoiced(serviceIds: number[]): Observable<void> {
  return this.httpClient.post<void>(
    `${this._url}/company/${this.companyId}/services/mark-invoiced`,
    { serviceIds },
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Get reference numbers for services
 * POST /company/{companyId}/services/reference-numbers
 */
getServiceReferenceNumbers(serviceIds: number[]): Observable<{serviceId: number; referenceNumber: string}[]> {
  return this.httpClient.post<{serviceId: number; referenceNumber: string}[]>(
    `${this._url}/company/${this.companyId}/services/reference-numbers`,
    { serviceIds },
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// ============================================
// REFERENCE NUMBERS AGGREGATE
// ============================================

/**
 * Get paginated reference number aggregates for the company
 * GET /api/companies/{companyId}/reference-numbers?pageNumber=1&pageSize=50&search={term}&year={year}&clientName={client}
 */
getReferenceNumbers(
  pageNumber: number = 1,
  pageSize: number = 50,
  search?: string,
  year?: number,
  clientName?: string
): Observable<PagedResponse<any>> {
  let params = new HttpParams()
    .set('pageNumber', pageNumber.toString())
    .set('pageSize', pageSize.toString());
  if (search) {
    params = params.set('search', search);
  }
  if (year) {
    params = params.set('year', year.toString());
  }
  if (clientName) {
    params = params.set('clientName', clientName);
  }
  return this.httpClient.get<PagedResponse<any>>(
    `${this._url}/api/companies/${this.companyId}/reference-numbers`,
    { params, headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Get paginated reference numbers for a specific submitted form (client)
 * GET /api/companies/{companyId}/reference-numbers/{submittedFormId}
 */
getReferenceNumbersByForm(
  submittedFormId: number,
  pageNumber: number = 1,
  pageSize: number = 50,
  search?: string,
  year?: number
): Observable<PagedResponse<any>> {
  let params = new HttpParams()
    .set('pageNumber', pageNumber.toString())
    .set('pageSize', pageSize.toString());
  if (search) {
    params = params.set('search', search);
  }
  if (year) {
    params = params.set('year', year.toString());
  }
  return this.httpClient.get<PagedResponse<any>>(
    `${this._url}/api/companies/${this.companyId}/reference-numbers/${submittedFormId}`,
    { params, headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Create invoicing transaction for services
 * POST /company/{companyId}/services/invoicing-transactions
 */
createInvoicingTransaction(transaction: {
  referenceNumber: string;
  transactionType: string;
  transactionNumber: string;
  transactionDate: string;
  totalAmount: number;
  status: string;
  sourceTransactionId: number;
}): Observable<void> {
  return this.httpClient.post<void>(
    `${this._url}/company/${this.companyId}/services/invoicing-transactions`,
    transaction,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Delete invoicing transaction
 * DELETE /company/{companyId}/services/invoicing-transactions/{sourceTransactionId}/{transactionType}
 */
deleteInvoicingTransaction(sourceTransactionId: number, transactionType: string): Observable<void> {
  return this.httpClient.delete<void>(
    `${this._url}/company/${this.companyId}/services/invoicing-transactions/${sourceTransactionId}/${transactionType}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// ============================================
// DEPARTMENT SERVICES (Service Templates)
// ============================================

/**
 * Get all services for a department
 * GET /company/{companyId}/departments/{departmentId}/services
 */
getDepartmentServices(departmentId: number): Observable<any[]> {
  return this.httpClient.get<any[]>(
    `${this._url}/company/${this.companyId}/departments/${departmentId}/services`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Create a new service template for a department
 * POST /company/{companyId}/departments/{departmentId}/services
 */
createDepartmentService(departmentId: number, service: {
  description: string;
  fee: number;
  serviceType: number;
  dueDateType: number;
  dueDateJson: string;
  account: string;
  vatInvoicingId: string;
  vatType: number;
  actualFee: number;
  invoicedOn: number;
  comments: string;
}): Observable<number> {
  return this.httpClient.post<number>(
    `${this._url}/company/${this.companyId}/departments/${departmentId}/services`,
    service,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Update an existing service template
 * PUT /company/{companyId}/departments/{departmentId}/services/{serviceId}
 */
updateDepartmentService(departmentId: number, serviceId: number, service: {
  description: string;
  fee: number;
  serviceType: number;
  dueDateType: number;
  dueDateJson: string;
  account: string;
  vatInvoicingId: string;
  vatType: number;
  actualFee: number;
  invoicedOn: number;
  comments: string;
}): Observable<void> {
  return this.httpClient.put<void>(
    `${this._url}/company/${this.companyId}/departments/${departmentId}/services/${serviceId}`,
    service,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Delete a service template from a department
 * DELETE /company/{companyId}/departments/{departmentId}/services/{serviceId}
 */
deleteDepartmentService(departmentId: number, serviceId: number): Observable<void> {
  return this.httpClient.delete<void>(
    `${this._url}/company/${this.companyId}/departments/${departmentId}/services/${serviceId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// Method 1: Get business license data
getBusinessLicenseData(formId: number): Observable<any> {
  // Mock for now, replace with HTTP call later
  return of({
    name: '',
    approvedOn: null,
    expiringDate: null,
    sequenceNo: null,
    validUntil: null
  }).pipe(delay(500));
}

// Method 2: Save business license data
saveBusinessLicenseData(formId: number, data: any): Observable<any> {
  // Mock for now, replace with HTTP call later
  return of({ success: true }).pipe(delay(500));
}

/**
 * Update customer OID for a submitted form
 * PATCH /company/{companyId}/forms/{submittedFormId}/customer-oid
 */
updateCustomerOid(submittedFormId: number, customerOid: string): Observable<void> {
  return this.httpClient.patch<void>(
    `${this._url}/company/${this.companyId}/forms/${submittedFormId}/customer-oid`,
    { customerOid },
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// Method 3: Get general information
getGeneralInformation(formId: number): Observable<any> {
  // Mock for now, replace with HTTP call later
  return of({
    potentialNames: [],
    businessType: {},
    contactInfo: {}
  }).pipe(delay(500));
}

// Method 4: Save general information
saveGeneralInformation(formId: number, data: any): Observable<any> {
  // Mock for now, replace with HTTP call later
  return of({ success: true }).pipe(delay(500));
}

// ============================================
// FAMILY MEMBERS METHODS
// ============================================

/**
 * Get all family members for a parent individual
 * GET /company/{companyId}/family-members/{parentSubmittedFormId}
 */
getFamilyMembers(parentSubmittedFormId: number): Observable<IFamilyMember[]> {
  return this.httpClient.get<IFamilyMember[]>(
    `${this._url}/company/${this.companyId}/family-members/${parentSubmittedFormId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Get all parent links for a given family member
 * GET /company/{companyId}/family-members/by-member/{familyMemberSubmittedFormId}
 */
getFamilyMembersByMember(familyMemberSubmittedFormId: number): Observable<IFamilyMember[]> {
  return this.httpClient.get<IFamilyMember[]>(
    `${this._url}/company/${this.companyId}/family-members/by-member/${familyMemberSubmittedFormId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Get a specific family member link by ID
 * GET /company/{companyId}/family-members/member/{id}
 */
getFamilyMemberById(id: number): Observable<IFamilyMember> {
  return this.httpClient.get<IFamilyMember>(
    `${this._url}/company/${this.companyId}/family-members/member/${id}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Add a new family member link
 * POST /company/{companyId}/family-members
 */
createFamilyMember(familyMember: IFamilyMember): Observable<IFamilyMember> {
  return this.httpClient.post<IFamilyMember>(
    `${this._url}/company/${this.companyId}/family-members`,
    familyMember,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Update a family member link by ID
 * PUT /company/{companyId}/family-members/member/{id}
 */
updateFamilyMember(id: number, familyMember: IFamilyMember): Observable<IFamilyMember> {
  return this.httpClient.put<IFamilyMember>(
    `${this._url}/company/${this.companyId}/family-members/member/${id}`,
    familyMember,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Delete a family member link by ID
 * DELETE /company/{companyId}/family-members/member/{id}
 */
deleteFamilyMember(id: number): Observable<void> {
  return this.httpClient.delete<void>(
    `${this._url}/company/${this.companyId}/family-members/member/${id}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// ============================================
// Employee Members CRUD
// ============================================

/**
 * Get all employees for a business
 * GET /company/{companyId}/employees/{parentSubmittedFormId}
 */
getEmployees(parentSubmittedFormId: number): Observable<IEmployee[]> {
  return this.httpClient.get<IEmployee[]>(
    `${this._url}/company/${this.companyId}/employees/${parentSubmittedFormId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Reverse lookup - get employee by member form ID
 * GET /company/{companyId}/employees/by-member/{employeeSubmittedFormId}
 */
getEmployeesByMember(employeeSubmittedFormId: number): Observable<IEmployee[]> {
  return this.httpClient.get<IEmployee[]>(
    `${this._url}/company/${this.companyId}/employees/by-member/${employeeSubmittedFormId}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Get a specific employee by ID
 * GET /company/{companyId}/employees/member/{id}
 */
getEmployeeById(id: number): Observable<IEmployee> {
  return this.httpClient.get<IEmployee>(
    `${this._url}/company/${this.companyId}/employees/member/${id}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Create a new employee
 * POST /company/{companyId}/employees
 */
createEmployee(employee: IEmployee): Observable<IEmployee> {
  return this.httpClient.post<IEmployee>(
    `${this._url}/company/${this.companyId}/employees`,
    employee,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Update an employee by ID
 * PUT /company/{companyId}/employees/member/{id}
 */
updateEmployee(id: number, employee: IEmployee): Observable<IEmployee> {
  return this.httpClient.put<IEmployee>(
    `${this._url}/company/${this.companyId}/employees/member/${id}`,
    employee,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

/**
 * Delete an employee by ID
 * DELETE /company/{companyId}/employees/member/{id}
 */
deleteEmployee(id: number): Observable<void> {
  return this.httpClient.delete<void>(
    `${this._url}/company/${this.companyId}/employees/member/${id}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

getPayrollEmployees(submittedFormId: number): Observable<IPayrollEmployee[]> {
  return this.httpClient.get<IPayrollEmployee[]>(
    `${this._url}/company/${this.companyId}/form/${submittedFormId}/payroll-employees`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

getPayrollEmployeeTypes(submittedFormId: number): Observable<IPayrollEmployeeType[]> {
  return this.httpClient.get<IPayrollEmployeeType[]>(
    `${this._url}/company/${this.companyId}/form/${submittedFormId}/payroll-employee-types`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

setPayrollDatabase(submittedFormId: number, payrollDatabase: string): Observable<void> {
  return this.httpClient.patch<void>(
    `${this._url}/company/${this.companyId}/forms/${submittedFormId}/payroll-database`,
    { payrollDatabase },
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

getPayrollPayslips(
  submittedFormId: number,
  year: number,
  month: number,
  employeeOid?: string | null
): Observable<IPayrollPayslip[]> {
  let params = new HttpParams()
    .set('year', year)
    .set('month', month);
  if (employeeOid) {
    params = params.set('employeeOid', employeeOid);
  }
  return this.httpClient.get<IPayrollPayslip[]>(
    `${this._url}/company/${this.companyId}/form/${submittedFormId}/payroll-payslips`,
    { headers: this.userHeaders, params }
  ).pipe(catchError(this.handleError));
}

downloadPayeMonthlyReport(
  submittedFormId: number,
  year: number,
  month: number
): Observable<HttpResponse<Blob>> {
  const params = new HttpParams()
    .set('year', year)
    .set('month', month);
  return this.httpClient.get(
    `${this._url}/company/${this.companyId}/form/${submittedFormId}/paye-monthly-export`,
    {
      headers: this.userHeaders,
      params,
      responseType: 'blob',
      observe: 'response'
    }
  ).pipe(catchError(this.handleError));
}

getPayrollPayslipYearly(
  submittedFormId: number,
  year: number,
  employeeOid: string
): Observable<IPayrollPayslipYearly> {
  const params = new HttpParams()
    .set('year', year)
    .set('employeeOid', employeeOid);
  return this.httpClient.get<IPayrollPayslipYearly>(
    `${this._url}/company/${this.companyId}/form/${submittedFormId}/payroll-payslip-yearly`,
    { headers: this.userHeaders, params }
  ).pipe(catchError(this.handleError));
}

getEmployeeExtraExpenses(employeeId: number): Observable<IEmployeeExtraExpense[]> {
  return this.httpClient.get<IEmployeeExtraExpense[]>(
    `${this._url}/company/${this.companyId}/employees/${employeeId}/extra-expenses`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

createEmployeeExtraExpense(
  employeeId: number,
  dto: IEmployeeExtraExpenseDto
): Observable<IEmployeeExtraExpense> {
  return this.httpClient.post<IEmployeeExtraExpense>(
    `${this._url}/company/${this.companyId}/employees/${employeeId}/extra-expenses`,
    dto,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

updateEmployeeExtraExpense(
  id: number,
  dto: IEmployeeExtraExpenseDto
): Observable<IEmployeeExtraExpense> {
  return this.httpClient.put<IEmployeeExtraExpense>(
    `${this._url}/company/${this.companyId}/employees/extra-expenses/${id}`,
    dto,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

deleteEmployeeExtraExpense(id: number): Observable<void> {
  return this.httpClient.delete<void>(
    `${this._url}/company/${this.companyId}/employees/extra-expenses/${id}`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// --- Payslip overrides (user edits layered on top of read-only vendor payroll DB) ---

getPayrollPayslipOverridesForMonth(
  submittedFormId: number,
  employeeOid: string,
  year: number,
  month: number
): Observable<IPayrollPayslipOverride[]> {
  let params = new HttpParams()
    .set('employeeOid', employeeOid)
    .set('year', year)
    .set('month', month);
  return this.httpClient.get<IPayrollPayslipOverride[]>(
    `${this._url}/company/${this.companyId}/form/${submittedFormId}/payroll-payslip-overrides/month`,
    { headers: this.userHeaders, params }
  ).pipe(catchError(this.handleError));
}

getPayrollPayslipOverridesForYear(
  submittedFormId: number,
  employeeOid: string,
  year: number
): Observable<IPayrollPayslipOverride[]> {
  let params = new HttpParams()
    .set('employeeOid', employeeOid)
    .set('year', year);
  return this.httpClient.get<IPayrollPayslipOverride[]>(
    `${this._url}/company/${this.companyId}/form/${submittedFormId}/payroll-payslip-overrides/year`,
    { headers: this.userHeaders, params }
  ).pipe(catchError(this.handleError));
}

savePayrollPayslipOverrides(
  submittedFormId: number,
  batch: IPayrollPayslipOverrideBatch
): Observable<IPayrollPayslipOverride[]> {
  return this.httpClient.put<IPayrollPayslipOverride[]>(
    `${this._url}/company/${this.companyId}/form/${submittedFormId}/payroll-payslip-overrides`,
    batch,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

// SubmittedForms with BusinessStatus = Employee.
// scope='company' (default) returns only this company's; scope='all' returns the system-wide list.
getEmployeeForms(scope: 'company' | 'all' = 'company'): Observable<IEmployeeFormOption[]> {
  const params = scope === 'all' ? new HttpParams().set('scope', 'all') : undefined;
  return this.httpClient.get<IEmployeeFormOption[]>(
    `${this._url}/company/${this.companyId}/forms/employees`,
    { headers: this.userHeaders, params }
  ).pipe(catchError(this.handleError));
}

/**
 * Get aggregated contacts (Business / Individual / Introducer) from all
 * submitted forms for the current company.
 * GET /company/{companyId}/forms/contacts
 */
getFormContacts(): Observable<IFormContact[]> {
  return this.httpClient.get<IFormContact[]>(
    `${this._url}/company/${this.companyId}/forms/contacts`,
    { headers: this.userHeaders }
  ).pipe(catchError(this.handleError));
}

}
